<template>
  <div class="card-selection">
    <em-header :headline="headline">
      <em-action-button
        :action="save"
        :disabled="!hasSelection"
      >
        {{ buttonLabel }}
      </em-action-button>
    </em-header>

    <div class="search">
      <el-input
        v-model="searchTerm"
        clearable
        placeholder="Search for a Material"
        prefix-icon="el-icon-search"
      />
    </div>

    <masonry
      v-loading.lock="searching"
      class="cards"
      :class="{locked}"
      :cols="masonryCols"
      :gutter="16"
    >
      <div
        v-for="material in materials"
        :key="material.id"
        class="container"
        :class="cardCssClasses(material.id)"
        @click="select(material.id)"
      >
        <el-card
          shadow="hover"
          class="card"
        >
          <div class="details">
            <h3>{{ material.name }}</h3>
          </div>

          <img :src="material.primary_rendition_url">

          <div class="overlay">
            <span class="label">Use {{ material.name }}</span>
          </div>

          <div class="details fixed right">
            <div class="detail-tag type-name">
              {{ material.material_type.label }}
            </div>
            <el-tag type="info">
              {{ material.id }}
            </el-tag>
            <div
              class="detail-tag"
              :style="getStatusStyle(material.status)"
            >
              {{ material.status.name }}
            </div>
          </div>
        </el-card>
      </div>
      <div
        v-if="!materials.length"
        class="no-results"
      >
        <span>Oh no - we did not find any Materials matching your criteria</span>
      </div>
    </masonry>

    <div
      v-if="hasNextPage"
      class="next-page"
    >
      <em-action-button :action="loadNextPage">
        Get me more
      </em-action-button>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  .cards
    .container
      margin 0 auto
      min-width 350px

  // A long status name wraps inside its tag instead of widening the row. The card
  // centres its content, so an unwrappable row spilled out and was clipped on both
  // sides, cutting off the start of the title as well as the end of the status.
  .detail-tag
    min-height 32px
    box-sizing border-box
    display flex
    align-items center
    padding 4px 12px
    border-radius 8px
    margin-left var(--gap)
    text-align center

  .details.fixed
    min-height 32px
    flex-wrap wrap
    row-gap var(--gap)

  .type-name
    padding 0
    margin 0 auto 0 0

  .next-page, .search
    margin calc(2 * var(--gap))
    text-align center
</style>

<script>
  import stopsLoading from '../../lib/mixins/stopsLoading'
  import EmHeader from '../../components/EmHeader'
  import EmActionButton from '../../components/EmActionButton'
  import { mapActions } from 'vuex'
  import hasDebounce from '../../mixins/hasDebounce'

  export default {
    name: 'SelectType',
    components: { EmActionButton, EmHeader },
    mixins: [
      stopsLoading,
      hasDebounce
    ],

    props: {
      paginator: {
        type: Object,
        required: true
      },
      buttonLabel: {
        type: String,
        required: true
      },
      headline: {
        type: String,
        required: true
      }
    },

    data () {
      return {
        selected: null,
        locked: false,
        materials: [],
        nextPage: null,
        searchTerm: '',
        searching: false,
        masonryCols: {
          default: 3,
          1100: 2,
          730: 1
        }
      }
    },

    computed: {
      hasSelection () {
        return !!this.selected
      },
      hasNextPage () {
        return this.nextPage !== null
      }
    },

    watch: {
      searchTerm () {
        this.debounces(this.search)
      }
    },

    created () {
      this.nextPage = this.paginator.next_page
      this.materials = this.paginator.items
    },

    methods: {
      ...mapActions('steps', [
        'execute',
        'mergeToState',
        'consumeState'
      ]),

      select (key) {
        if (this.locked) {
          return
        }

        this.selected = key
      },

      loadNextPage () {
        return this.loadPage(this.nextPage)
      },

      loadPage (page) {
        this.mergeToState({
          page
        })

        if (this.searchTerm) {
          this.mergeToState({
            search: this.searchTerm
          })
        }

        return this.execute().then(state => {
          this.materials = this.materials.concat(state.paginator.items)
          this.nextPage = state.paginator.next_page
          this.searching = false
        })
      },

      save () {
        this.locked = true

        // update the step state
        this.mergeToState({
          material_id: this.selected
        })

        return this.execute()
      },

      cardCssClasses (id) {
        return {
          selected: this.selected === id
        }
      },

      getStatusStyle (status) {
        return {
          backgroundColor: status.background,
          color: status.foreground
        }
      },

      search () {
        this.selected = null
        this.searching = true

        if (!this.searchTerm) {
          this.materials = []
          return this.loadPage(1)
        }

        this.mergeToState({
          search: this.searchTerm,
          page: null
        })

        return this.execute().then(state => {
          this.materials = state.paginator.items
          this.nextPage = state.paginator.next_page
          this.searching = false
        })
      }
    }
  }
</script>
